// client/src/components/leads/b2c/CustomerDashboard.js
// ULTRA COMPACT + PROFESSIONAL: No scroll, smaller cards, fixed source chart

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { customerAPI } from '../../../services/customerAPI';

export default function CustomerDashboard({ customers }) {
  const [retentionMetrics, setRetentionMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);

  useEffect(() => {
    loadRetentionMetrics();
  }, []);

  const loadRetentionMetrics = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getRetentionMetrics();
      if (response.success) {
        setRetentionMetrics(response.data);
      }
    } catch (error) {
      console.error('Error loading retention metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = () => {
    if (!customers || customers.length === 0) {
      return {
        total: 0,
        thisWeek: 0,
        thisMonth: 0,
        lastMonth: 0,
        growthRate: 0,
        sourceBreakdown: []
      };
    }

    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const lastMonthStart = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    const thisWeek = customers.filter(c => new Date(c.createdAt || c.created_at) >= weekAgo).length;
    const thisMonth = customers.filter(c => new Date(c.createdAt || c.created_at) >= monthAgo).length;
    const lastMonth = customers.filter(c => {
      const date = new Date(c.createdAt || c.created_at);
      return date >= lastMonthStart && date < monthAgo;
    }).length;

    const growthRate = lastMonth > 0 
      ? Math.round(((thisMonth - lastMonth) / lastMonth) * 100)
      : thisMonth > 0 ? 100 : 0;

    // FIXED: Better source counting with safety checks
    const sourceCounts = {};
    customers.forEach(c => {
      const source = (c.source || 'other').toLowerCase();
      sourceCounts[source] = (sourceCounts[source] || 0) + 1;
    });

    const sourceBreakdown = Object.entries(sourceCounts)
      .map(([source, count]) => ({
        source,
        count,
        percentage: customers.length > 0 ? Math.round((count / customers.length) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return { total: customers.length, thisWeek, thisMonth, lastMonth, growthRate, sourceBreakdown };
  };

  const analytics = calculateAnalytics();

  const getSourceColor = (source) => {
    const colors = {
      instagram: '#9333ea',
      facebook: '#2563eb',
      linkedin: '#1d4ed8',
      whatsapp: '#16a34a',
      referral: '#059669',
      website: '#6b7280',
      email: '#eab308',
      phone: '#6366f1',
      walkin: '#ea580c',
      other: '#6b7280'
    };
    return colors[source?.toLowerCase()] || '#6b7280';
  };

  const formatCurrency = (amount) => `£${parseFloat(amount || 0).toFixed(2)}`;

  // Compact Tooltip component
  const Tooltip = ({ id, title, children }) => (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setActiveTooltip(id)}
        onMouseLeave={() => setActiveTooltip(null)}
        onClick={(e) => {
          e.preventDefault();
          setActiveTooltip(activeTooltip === id ? null : id);
        }}
        className="ml-1 text-gray-400 hover:text-blue-600 cursor-help transition-colors"
        type="button"
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
      {activeTooltip === id && (
        <div className="absolute z-50 left-0 bottom-full mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg p-2.5 shadow-xl border border-gray-700">
          <div className="font-semibold mb-1">{title}</div>
          <div className="text-gray-300 leading-relaxed">{children}</div>
          <div className="absolute -bottom-1 left-3 w-2 h-2 bg-gray-900 transform rotate-45 border-r border-b border-gray-700"></div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4 space-y-3 max-h-screen overflow-hidden">

      {/* Compact Header */}
      <div className="pb-2 border-b border-gray-200">
        <h2 className="text-sm font-bold text-gray-900">Customer Analytics Dashboard</h2>
        <p className="text-xs text-gray-500 mt-0.5">Track growth, retention, revenue, and customer health</p>
      </div>

      {/* SECTION 1: CUSTOMER GROWTH - More Compact */}
      <div>
        <div className="flex items-center mb-2">
          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Customer Growth</h3>
          <Tooltip id="growth-section" title="Customer Growth">
            Track how your customer base is expanding over time.
          </Tooltip>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {/* Total */}
          <div className="group relative bg-white rounded-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden">
            {/* Dark Header with Grid */}
            <div className="relative h-10 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
              <div className="absolute top-0 left-0 w-16 h-16 pointer-events-none opacity-50">
                <div style={{
                  backgroundImage: `
                    linear-gradient(#3b82f6 0.5px, transparent 0.5px),
                    linear-gradient(90deg, #3b82f6 0.5px, transparent 0.5px)
                  `,
                  backgroundSize: '6px 6px'
                }} className="absolute inset-0"></div>
                <div style={{
                  background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)'
                }} className="absolute inset-0"></div>
              </div>

              <div className="relative z-10 h-full flex items-center justify-between px-2.5">
                <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Total</p>
                <Tooltip id="total" title="Total Customers">
                  Complete number of customers in your database.
                </Tooltip>
              </div>
            </div>

            {/* White Content Area */}
            <div className="p-2.5">
              <p className="text-2xl font-bold text-gray-900 leading-none">{analytics.total}</p>
              <p className="text-xs text-gray-500 mt-1">Customers</p>
            </div>
          </div>

          {/* This Week */}
          <div className="group relative bg-white rounded-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden">
            {/* Dark Header with Grid */}
            <div className="relative h-10 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
              <div className="absolute top-0 left-0 w-16 h-16 pointer-events-none opacity-50">
                <div style={{
                  backgroundImage: `
                    linear-gradient(#16a34a 0.5px, transparent 0.5px),
                    linear-gradient(90deg, #16a34a 0.5px, transparent 0.5px)
                  `,
                  backgroundSize: '6px 6px'
                }} className="absolute inset-0"></div>
                <div style={{
                  background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)'
                }} className="absolute inset-0"></div>
              </div>

              <div className="relative z-10 h-full flex items-center justify-between px-2.5">
                <p className="text-xs font-bold text-white/60 uppercase tracking-wider">This Week</p>
                <Tooltip id="week" title="This Week">
                  New customers in the last 7 days.
                </Tooltip>
              </div>
            </div>

            {/* White Content Area */}
            <div className="p-2.5">
              <p className="text-2xl font-bold text-gray-900 leading-none">+{analytics.thisWeek}</p>
              <p className="text-xs text-gray-500 mt-1">New (7 days)</p>
            </div>
          </div>

          {/* This Month */}
          <div className="group relative bg-white rounded-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden">
            {/* Dark Header with Grid */}
            <div className="relative h-10 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
              <div className="absolute top-0 left-0 w-16 h-16 pointer-events-none opacity-50">
                <div style={{
                  backgroundImage: `
                    linear-gradient(#9333ea 0.5px, transparent 0.5px),
                    linear-gradient(90deg, #9333ea 0.5px, transparent 0.5px)
                  `,
                  backgroundSize: '6px 6px'
                }} className="absolute inset-0"></div>
                <div style={{
                  background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)'
                }} className="absolute inset-0"></div>
              </div>

              <div className="relative z-10 h-full flex items-center justify-between px-2.5">
                <p className="text-xs font-bold text-white/60 uppercase tracking-wider">This Month</p>
                <Tooltip id="month" title="This Month">
                  New customers in last 30 days (vs {analytics.lastMonth} previous month).
                </Tooltip>
              </div>
            </div>

            {/* White Content Area */}
            <div className="p-2.5">
              <p className="text-2xl font-bold text-gray-900 leading-none">+{analytics.thisMonth}</p>
              <p className="text-xs text-gray-500 mt-1">New (30 days)</p>
            </div>
          </div>

          {/* Growth */}
          <div className="group relative bg-white rounded-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden">
            {/* Dark Header with Grid */}
            <div className="relative h-10 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
              <div className="absolute top-0 left-0 w-16 h-16 pointer-events-none opacity-50">
                <div style={{
                  backgroundImage: `
                    linear-gradient(${analytics.growthRate >= 0 ? '#16a34a' : '#dc2626'} 0.5px, transparent 0.5px),
                    linear-gradient(90deg, ${analytics.growthRate >= 0 ? '#16a34a' : '#dc2626'} 0.5px, transparent 0.5px)
                  `,
                  backgroundSize: '6px 6px'
                }} className="absolute inset-0"></div>
                <div style={{
                  background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)'
                }} className="absolute inset-0"></div>
              </div>

              <div className="relative z-10 h-full flex items-center justify-between px-2.5">
                <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Growth</p>
                <div className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                  analytics.growthRate >= 0 ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
                }`}>
                  {analytics.growthRate >= 0 ? '+' : ''}{analytics.growthRate}%
                </div>
              </div>
            </div>

            {/* White Content Area */}
            <div className="p-2.5">
              <p className={`text-2xl font-bold leading-none ${analytics.growthRate >= 0 ? 'text-gray-900' : 'text-gray-900'}`}>
                {analytics.growthRate >= 0 ? '+' : ''}{analytics.growthRate}%
              </p>
              <p className="text-xs text-gray-500 mt-1">MoM Growth</p>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: CUSTOMER SEGMENTS - More Compact */}
      {retentionMetrics && (
        <div>
          <div className="flex items-center mb-2">
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Customer Segments</h3>
            <Tooltip id="segments-section" title="Customer Segments">
              Auto-categorized by engagement and value.
            </Tooltip>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {/* VIP */}
            <div className="group relative bg-white rounded-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden">
              {/* Dark Header with Grid */}
              <div className="relative h-10 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
                <div className="absolute top-0 left-0 w-16 h-16 pointer-events-none opacity-50">
                  <div style={{
                    backgroundImage: `
                      linear-gradient(#eab308 0.5px, transparent 0.5px),
                      linear-gradient(90deg, #eab308 0.5px, transparent 0.5px)
                    `,
                    backgroundSize: '6px 6px'
                  }} className="absolute inset-0"></div>
                  <div style={{
                    background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)'
                  }} className="absolute inset-0"></div>
                </div>

                <div className="relative z-10 h-full flex items-center justify-between px-2.5">
                  <p className="text-xs font-bold text-white/60 uppercase tracking-wider">VIP</p>
                  <div className="px-1.5 py-0.5 rounded text-xs font-bold bg-yellow-600/20 text-yellow-400">
                    <i className="fas fa-crown"></i>
                  </div>
                </div>
              </div>

              {/* White Content Area */}
              <div className="p-2.5">
                <p className="text-2xl font-bold text-gray-900 leading-none">{retentionMetrics.vipCount || 0}</p>
                <p className="text-xs text-gray-500 mt-1">High Value</p>
              </div>
            </div>

            {/* Dormant */}
            <div className="group relative bg-white rounded-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden">
              {/* Dark Header with Grid */}
              <div className="relative h-10 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
                <div className="absolute top-0 left-0 w-16 h-16 pointer-events-none opacity-50">
                  <div style={{
                    backgroundImage: `
                      linear-gradient(#ea580c 0.5px, transparent 0.5px),
                      linear-gradient(90deg, #ea580c 0.5px, transparent 0.5px)
                    `,
                    backgroundSize: '6px 6px'
                  }} className="absolute inset-0"></div>
                  <div style={{
                    background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)'
                  }} className="absolute inset-0"></div>
                </div>

                <div className="relative z-10 h-full flex items-center justify-between px-2.5">
                  <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Dormant</p>
                  <div className="px-1.5 py-0.5 rounded text-xs font-bold bg-orange-600/20 text-orange-400">
                    <i className="fas fa-moon"></i>
                  </div>
                </div>
              </div>

              {/* White Content Area */}
              <div className="p-2.5">
                <p className="text-2xl font-bold text-gray-900 leading-none">{retentionMetrics.dormantCount || 0}</p>
                <p className="text-xs text-gray-500 mt-1">60+ Days Inactive</p>
              </div>
            </div>

            {/* At Risk */}
            <div className="group relative bg-white rounded-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden">
              {/* Dark Header with Grid */}
              <div className="relative h-10 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
                <div className="absolute top-0 left-0 w-16 h-16 pointer-events-none opacity-50">
                  <div style={{
                    backgroundImage: `
                      linear-gradient(#dc2626 0.5px, transparent 0.5px),
                      linear-gradient(90deg, #dc2626 0.5px, transparent 0.5px)
                    `,
                    backgroundSize: '6px 6px'
                  }} className="absolute inset-0"></div>
                  <div style={{
                    background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)'
                  }} className="absolute inset-0"></div>
                </div>

                <div className="relative z-10 h-full flex items-center justify-between px-2.5">
                  <p className="text-xs font-bold text-white/60 uppercase tracking-wider">At Risk</p>
                  <div className="px-1.5 py-0.5 rounded text-xs font-bold bg-red-600/20 text-red-400">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                </div>
              </div>

              {/* White Content Area */}
              <div className="p-2.5">
                <p className="text-2xl font-bold text-gray-900 leading-none">{retentionMetrics.atRiskCount || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Action Needed</p>
              </div>
            </div>

            {/* Revenue */}
            <div className="group relative bg-white rounded-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-gray-200 overflow-hidden">
              {/* Dark Header with Grid */}
              <div className="relative h-10 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
                <div className="absolute top-0 left-0 w-16 h-16 pointer-events-none opacity-50">
                  <div style={{
                    backgroundImage: `
                      linear-gradient(#16a34a 0.5px, transparent 0.5px),
                      linear-gradient(90deg, #16a34a 0.5px, transparent 0.5px)
                    `,
                    backgroundSize: '6px 6px'
                  }} className="absolute inset-0"></div>
                  <div style={{
                    background: 'linear-gradient(135deg, transparent 0%, #0f172a 70%)'
                  }} className="absolute inset-0"></div>
                </div>

                <div className="relative z-10 h-full flex items-center justify-between px-2.5">
                  <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Revenue</p>
                  <div className="px-1.5 py-0.5 rounded text-xs font-bold bg-green-600/20 text-green-400">
                    <i className="fas fa-pound-sign"></i>
                  </div>
                </div>
              </div>

              {/* White Content Area */}
              <div className="p-2.5">
                <p className="text-xl font-bold text-gray-900 leading-none">{formatCurrency(retentionMetrics.totalRevenue || 0)}</p>
                <p className="text-xs text-gray-500 mt-1">Total Revenue</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 3: RETENTION & ACQUISITION - Ultra Compact */}
      <div className="grid grid-cols-2 gap-3">

        {/* Retention Insights */}
        {retentionMetrics && (
          <div className="bg-white border-2 border-gray-200 rounded-lg p-3">
            <div className="flex items-center mb-2">
              <h3 className="text-xs font-bold text-gray-900">Retention Insights</h3>
              <Tooltip id="retention-section" title="Retention Insights">
                Key metrics on customer engagement and value.
              </Tooltip>
            </div>
            <div className="space-y-1.5">
              {/* Returning */}
              <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">Returning</span>
                  <Tooltip id="returning" title="Returning Customers">
                    {retentionMetrics.withVisits || 0} out of {retentionMetrics.totalCustomers || 0} have made visits.
                  </Tooltip>
                </div>
                <span className="font-bold text-blue-900">
                  {retentionMetrics.withVisits || 0} ({retentionMetrics.totalCustomers > 0 ? Math.round((retentionMetrics.withVisits / retentionMetrics.totalCustomers) * 100) : 0}%)
                </span>
              </div>

              {/* LTV */}
              <div className="flex items-center justify-between text-xs py-1 border-b border-gray-100">
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">Avg LTV</span>
                  <Tooltip id="ltv" title="Average Lifetime Value">
                    {formatCurrency(retentionMetrics.totalRevenue || 0)} ÷ {retentionMetrics.totalCustomers || 0} = {formatCurrency(retentionMetrics.averageLifetimeValue || 0)}
                  </Tooltip>
                </div>
                <span className="font-bold text-green-900">{formatCurrency(retentionMetrics.averageLifetimeValue || 0)}</span>
              </div>

              {/* Projected */}
              <div className="flex items-center justify-between text-xs py-1">
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">Projected (30d)</span>
                  <Tooltip id="projected" title="30-Day Projection">
                    AI-predicted revenue based on customer patterns.
                  </Tooltip>
                </div>
                <span className="font-bold text-purple-900">{formatCurrency(retentionMetrics.projectedRevenue30Days || 0)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Top Sources - FIXED */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-3">
          <div className="flex items-center mb-2">
            <h3 className="text-xs font-bold text-gray-900">Top Acquisition Sources</h3>
            <Tooltip id="sources-section" title="Acquisition Sources">
              Where your customers are coming from.
            </Tooltip>
          </div>
          {!analytics.sourceBreakdown || analytics.sourceBreakdown.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-chart-bar text-gray-300 text-2xl mb-1"></i>
              <p className="text-xs text-gray-500">No data yet</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {analytics.sourceBreakdown.map((item) => (
                <div key={item.source} className="group">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-medium text-gray-800 capitalize">{item.source}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-600">{item.count}</span>
                      <span className="text-xs font-semibold text-gray-800">({item.percentage}%)</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500 ease-out"
                      style={{ 
                        width: `${item.percentage}%`,
                        backgroundColor: getSourceColor(item.source)
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

    </div>
  );
}

CustomerDashboard.propTypes = {
  customers: PropTypes.array.isRequired
};

